import { useRef, useState } from 'react'

import { PktButton, PktCheckbox, PktRadioButton, PktSelect, PktTextinput } from '@/components'
import { PktFileUpload } from '@/components/fileupload/FileUpload'
import { FileItem, TFileItemList, TFileTransfer } from '@/components/fileupload/types'

export default function FileUpload() {
  const [files, setFiles] = useState<Array<FileItem>>([])
  const [multiple, setMultiple] = useState(true)
  const [transfers, setTransfers] = useState<Array<TFileTransfer>>([])

  const [truncateTail, setTruncateTail] = useState<number | undefined>(4)
  const [itemRenderer, setItemRenderer] = useState<'filename' | 'thumbnail'>('filename')
  const [capture, setCapture] = useState<'user' | 'environment' | 'ingen'>('ingen')
  const [renameEnabled, setRenameEnabled] = useState(true)
  const [commentEnabled, setCommentEnabled] = useState(true)
  const [isDisabled, setIsDisabled] = useState(false)
  const [enableImagePreview, setEnableImagePreview] = useState(true)
  // Upload simulation
  const [uploadSpeed, setUploadSpeed] = useState(0)
  const [simulateError, setSimulateError] = useState(false)
  const [showProgressBar, setShowProgressBar] = useState(true)
  const uploadIntervals = useRef<Map<string, NodeJS.Timeout>>(new Map())

  // Use refs to avoid stale closures in setInterval
  const simulateErrorRef = useRef(simulateError)
  const showProgressBarRef = useRef(showProgressBar)
  simulateErrorRef.current = simulateError
  showProgressBarRef.current = showProgressBar

  // Validation
  const [enableValidation, setEnableValidation] = useState(false)
  const [allowedFormatsInput, setAllowedFormatsInput] = useState<string>('pdf, jpg, jpeg, png, gif, webp')

  const parseAllowedFormats = (input: string): string[] | undefined => {
    const raw = input.trim()
    if (!raw) return undefined

    const tryParse = (candidate: string): string[] | undefined => {
      try {
        const parsed = JSON.parse(candidate)
        if (!Array.isArray(parsed)) return undefined
        const normalized = parsed
          .map((item) => String(item).trim())
          .map((item) => item.replace(/^\./, '').toLowerCase())
          .filter(Boolean)
        return normalized.length ? normalized : undefined
      } catch {
        return undefined
      }
    }

    const parsedArray = tryParse(raw) ?? tryParse(raw.replace(/'/g, '"'))
    if (parsedArray) return parsedArray

    const quotedTokens = [...raw.matchAll(/['"]([^'"]+)['"]/g)]
      .map((match) => match[1].trim())
      .map((item) => item.replace(/^\./, '').toLowerCase())
      .filter(Boolean)
    if (quotedTokens.length > 1) return quotedTokens

    const formats = raw
      .replace(/^\[/, '')
      .replace(/\]$/, '')
      .split(',')
      .map((part) => part.trim().replace(/^['"]|['"]$/g, ''))
      .map((item) => item.replace(/^\./, '').toLowerCase())
      .filter(Boolean)
    return formats.length ? formats : undefined
  }

  const allowedFormats = parseAllowedFormats(allowedFormatsInput ?? '')
  const allowedFormatsAccept = allowedFormats?.length
    ? allowedFormats
        .map((format) => {
          const normalized = format.toLowerCase()
          if (normalized.includes('/')) return normalized
          if (normalized.startsWith('.')) return normalized
          return `.${normalized}`
        })
        .join(', ')
    : undefined

  const customValidation = (file: File): string | null => {
    if (file.name.includes(' ')) {
      return `Filnavnet "${file.name}" kan ikke inneholde mellomrom.`
    }
    return null
  }

  const updateProgress = (
    fileId: string,
    progress: TFileTransfer['progress'],
    errorMessage?: string,
    showProgress?: boolean,
    lastProgress?: number,
  ) => {
    setTransfers((prev) => {
      const idx = prev.findIndex((t) => t.fileId === fileId)
      if (idx >= 0) {
        const updated = [...prev]
        // Store last numeric progress when transitioning to error
        const currentLastProgress =
          progress === 'error' && typeof updated[idx].progress === 'number'
            ? updated[idx].progress
            : (lastProgress ?? updated[idx].lastProgress)
        updated[idx] = {
          ...updated[idx],
          progress,
          errorMessage,
          showProgress: showProgress ?? updated[idx].showProgress,
          lastProgress: currentLastProgress,
        }
        return updated
      }
      return [...prev, { fileId, progress, errorMessage, showProgress, lastProgress }]
    })
  }

  const simulateUpload = (fileItem: FileItem) => {
    let progress = 0
    let ticks = 0
    const initialShowProgressBar = showProgressBarRef.current
    // Pass showProgressBar to track the mode for error display later
    updateProgress(fileItem.fileId, initialShowProgressBar ? 0.01 : 0, undefined, initialShowProgressBar)

    const interval = setInterval(() => {
      ticks++
      // Use refs to get current values (avoids stale closure)
      const shouldError = simulateErrorRef.current

      if (initialShowProgressBar) {
        progress += 0.05

        if (shouldError && progress >= 0.9) {
          clearInterval(interval)
          uploadIntervals.current.delete(fileItem.fileId)
          updateProgress(fileItem.fileId, 'error', 'Feil under opplastning')
          return
        }

        if (progress >= 1) {
          clearInterval(interval)
          uploadIntervals.current.delete(fileItem.fileId)
          updateProgress(fileItem.fileId, 'done')
        } else {
          updateProgress(fileItem.fileId, progress)
        }
      } else {
        // Indeterminate mode: stay at 0 until "complete" (after ~20 ticks)
        if (shouldError && ticks >= 19) {
          clearInterval(interval)
          uploadIntervals.current.delete(fileItem.fileId)
          updateProgress(fileItem.fileId, 'error', 'Feil under opplastning')
          return
        }

        if (ticks >= 20) {
          clearInterval(interval)
          uploadIntervals.current.delete(fileItem.fileId)
          updateProgress(fileItem.fileId, 'done')
        }
      }
    }, uploadSpeed)

    uploadIntervals.current.set(fileItem.fileId, interval)
  }

  const handleUploadRequested = (fileItem: FileItem) => simulateUpload(fileItem)

  const handleTransferCancelled = (fileId: string) => {
    const interval = uploadIntervals.current.get(fileId)
    if (interval) {
      clearInterval(interval)
      uploadIntervals.current.delete(fileId)
    }
    setTransfers((prev) => prev.filter((t) => t.fileId !== fileId))
    setFiles((prev) => prev.filter((f) => f.fileId !== fileId))
  }

  return (
    <main
      className="page-main pkt-container"
      style={{ backgroundColor: 'var(--pkt-color-background-default)', paddingBlock: '2rem' }}
    >
      <h1>FileUpload Demo</h1>

      <div style={{ display: 'flex', flexDirection: 'row', gap: '2rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <fieldset>
          <legend>Komponent-innstillinger</legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
            <PktCheckbox
              isSwitch
              id="multiple"
              label="Tillat flere filer"
              checked={multiple}
              onChange={(e) => setMultiple(e.target.checked)}
            />
            <PktCheckbox
              isSwitch
              id="rename"
              label="Tillat endring av filnavn"
              checked={renameEnabled}
              onChange={(e) => setRenameEnabled(e.target.checked)}
            />
            <PktCheckbox
              isSwitch
              id="comments"
              label="Tillat kommentarer"
              checked={commentEnabled}
              onChange={(e) => setCommentEnabled(e.target.checked)}
            />
            <div>
              <PktCheckbox
                isSwitch
                id="disabled"
                label="Deaktivert"
                checked={isDisabled}
                onChange={(e) => setIsDisabled(e.target.checked)}
              />
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#666' }}>
                Deaktiverer hele komponenten - ingen interaksjon mulig
              </p>
            </div>
            <label>
              Truncate tail:
              <input
                type="number"
                min={0}
                max={20}
                value={truncateTail}
                onChange={(e) => setTruncateTail(parseInt(e.target.value))}
                style={{ width: '60px', marginLeft: '0.5rem' }}
              />
            </label>
            <div>
              <span style={{ fontWeight: 500, marginBottom: '0.25rem', display: 'block' }}>Visning</span>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <PktRadioButton
                  id="itemRenderer-filename"
                  name="itemRenderer"
                  label="Filnavn"
                  value="filename"
                  checked={itemRenderer === 'filename'}
                  onChange={() => setItemRenderer('filename')}
                />
                <PktRadioButton
                  id="itemRenderer-thumbnail"
                  name="itemRenderer"
                  label="Miniatyrbilde"
                  value="thumbnail"
                  checked={itemRenderer === 'thumbnail'}
                  onChange={() => setItemRenderer('thumbnail')}
                />
              </div>
            </div>
            {itemRenderer === 'thumbnail' && (
              <div>
                <PktCheckbox
                  isSwitch
                  id="imagePreview"
                  label="Forhåndsvisning av bilder"
                  checked={enableImagePreview}
                  onChange={(e) => setEnableImagePreview(e.target.checked)}
                />
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#666' }}>
                  Klikk på miniatyrbilder for å se fullstendig bilde i modal
                </p>
              </div>
            )}
          </div>
        </fieldset>

        <fieldset>
          <legend>Testing og simulering</legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
            <label>
              Opplastingshastighet (ms/5%):
              <input
                type="number"
                min={50}
                max={5000}
                step={50}
                value={uploadSpeed}
                onChange={(e) => setUploadSpeed(parseInt(e.target.value))}
                style={{ width: '80px', marginLeft: '0.5rem' }}
              />
              <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                ({Math.round((100 / 5) * (uploadSpeed / 1000))}s)
              </span>
            </label>

            <div>
              <PktCheckbox
                isSwitch
                id="showProgressBar"
                label="Vis fremdriftsindikator"
                checked={showProgressBar}
                onChange={(e) => setShowProgressBar(e.target.checked)}
              />
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#666' }}>
                Av = "Laster opp...", På = fremdriftslinje med prosent
              </p>
            </div>

            <PktCheckbox
              isSwitch
              id="simulateError"
              label="Simuler feil ved 90%"
              checked={simulateError}
              onChange={(e) => setSimulateError(e.target.checked)}
            />
            <div>
              <PktCheckbox
                isSwitch
                id="validation"
                label="Aktiver validering"
                checked={enableValidation}
                onChange={(e) => setEnableValidation(e.target.checked)}
              />
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#666' }}>
                Kun PDF/bilder, maks 1MB, ingen mellomrom i filnavn
              </p>
            </div>
            <PktTextinput
              id="allowedFormats"
              label="Tillatte formater"
              value={allowedFormatsInput}
              onChange={(e) => setAllowedFormatsInput(e.currentTarget.value)}
              placeholder='pdf, jpg, image/* eller ["jpg", "png"]'
            />
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#666' }}>
              Eksempel: <code>jpg, pdf, image/*</code> eller <code>["jpg","png"]</code>.
            </p>

            <PktSelect
              id="capture"
              label="Kamera"
              value={capture}
              onChange={(e) => setCapture(e.target.value as 'user' | 'environment' | 'ingen')}
              style={{ width: '50%', marginTop: '0.5rem' }}
            >
              <option value="ingen">Ingen</option>
              <option value="user">Selfie</option>
              <option value="environment">Bakover</option>
            </PktSelect>
          </div>
        </fieldset>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', flexWrap: 'wrap' }}>
        <div>
          <h2>File Upload component</h2>
          {/*
            Form wraps the fileupload for traditional server submissions.
            - target="uploadTarget" loads the response in the iframe (no page navigation)
            - action points to your upload endpoint
            - encType="multipart/form-data" is required for file uploads
            - Note: with `uploadStrategy="custom"` the form submits file IDs (hidden inputs),
              not the files themselves. Use `uploadStrategy="form"` if you want to POST files via the form.
          */}
          <form method="post" target="uploadTarget" action="http://localhost:3000/upload" encType="multipart/form-data">
            <PktFileUpload
              id="fileupload"
              name="files"
              label="Her kan du laste opp filer"
              helptext={`Simulert opplasting: ${uploadSpeed}ms per 5% (${Math.round((100 / 5) * (uploadSpeed / 1000))}s totalt)${simulateError ? ' - Feil ved 90%' : ''}`}
              value={files}
              onFilesChanged={(newFiles: TFileItemList) => setFiles(newFiles)}
              multiple={multiple}
              capture={capture === 'ingen' ? undefined : capture}
              itemRenderer={itemRenderer}
              truncateTail={truncateTail}
              renameFilesEnabled={renameEnabled}
              addCommentsEnabled={commentEnabled}
              fullWidth={true}
              uploadStrategy="custom"
              transfers={transfers}
              onFileUploadRequested={handleUploadRequested}
              onTransferCancelled={handleTransferCancelled}
              accept={allowedFormatsAccept}
              allowedFormats={enableValidation ? allowedFormats : undefined}
              maxFileSize={enableValidation ? '1MB' : undefined}
              onFileValidation={enableValidation ? customValidation : undefined}
              disabled={isDisabled}
              enableImagePreview={enableImagePreview}
            />

            <PktButton type="submit" skin="primary">
              Last opp filer
            </PktButton>
          </form>
        </div>

        <div style={{ width: '50%' }}>
          <h2>Server response (iframe)</h2>
          <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
            Når du klikker "Last opp filer", sendes skjemaet til serveren og svaret vises her. Dette fungerer kun hvis
            du har en server som kjører på localhost:3000/upload.
          </p>
          <iframe
            title="uploadTarget"
            name="uploadTarget"
            style={{ height: 200, width: '100%', border: '1px solid #ccc' }}
          />
        </div>
      </div>
    </main>
  )
}
